import { useState, useEffect, useRef, useCallback, SyntheticEvent } from 'react';
import ReactPlayer from 'react-player';
import {
  Play,
  Pause,
  Bookmark,
  X,
  Maximize,
  Minimize
} from 'lucide-react';

import videoStyles from './VideoPlayer.module.css';
import { logTrace, logError, logEvent } from '../../utils/trackingUtils';
import { VideoLoadError } from './VideoLoadError';

export interface VideoPlayerParams {
  videoUrl: string;
  startTimeSeconds: number;
  endTimeSeconds: number;
  segmentTitle: string;
  procedureType?: string;
  onBookmark?: (time: number) => void;
  onClose?: () => void;
  isFullscreenMode?: boolean;
  autoPlay?: boolean;
  caseId?: string;
}

export const VideoPlayer = ({
  videoUrl,
  startTimeSeconds,
  endTimeSeconds,
  segmentTitle,
  procedureType = '',
  onBookmark,
  onClose,
  isFullscreenMode = false,
  autoPlay = true,
  caseId,
}: VideoPlayerParams): React.JSX.Element => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  const [isPlaying, setIsPlaying] = useState<boolean>(autoPlay);
  const [currentTime, setCurrentTime] = useState<number>(startTimeSeconds);
  const [duration, setDuration] = useState<number>(0);
  const [segmentDuration, setSegmentDuration] = useState<number>(0);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(isFullscreenMode);
  const [showControls, setShowControls] = useState<boolean>(true);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isBuffering, setIsBuffering] = useState<boolean>(true);
  const [error, setError] = useState<'load_error' | null>(null);

  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Format time in HH:MM:SS or MM:SS
  const formatTime = useCallback((timeInSeconds: number, showNegative = false): string => {
    if (isNaN(timeInSeconds) || timeInSeconds < 0) {
      const zeroTime = timeInSeconds >= 3600 || segmentDuration >= 3600 ? '00:00:00' : '00:00';
      return showNegative ? `-${zeroTime}` : zeroTime;
    }

    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = Math.floor(timeInSeconds % 60);

    let timeString: string;
    if (hours > 0 || segmentDuration >= 3600) {
      timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } else {
      timeString = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return showNegative ? `-${timeString}` : timeString;
  }, [segmentDuration]);

  // Get normalized time (relative to segment start)
  const getNormalizedTime = useCallback((): number => {
    return Math.max(0, currentTime - startTimeSeconds);
  }, [currentTime, startTimeSeconds]);

  // Get remaining time as negative string
  const getRemainingTime = useCallback((): string => {
    const remaining = segmentDuration - getNormalizedTime();
    return formatTime(remaining, true);
  }, [segmentDuration, getNormalizedTime, formatTime]);

  // Handle play/pause - ReactPlayer is controlled via playing prop
  const handlePlayPause = useCallback((): void => {
    const newPlayingState = !isPlaying;
    setIsPlaying(newPlayingState);
    logTrace('VideoPlayer', newPlayingState ? 'video_play_' + segmentTitle : 'video_pause_' + segmentTitle, segmentTitle);
  }, [isPlaying, segmentTitle]);

  // Handle skip forward (10 seconds)
  const handleSkipForward = useCallback((): void => {
    const video = videoRef.current;
    if (!video) return;

    const effectiveEndTime = endTimeSeconds > startTimeSeconds ? endTimeSeconds : duration;
    const newTime = Math.min(currentTime + 10, effectiveEndTime);
    video.currentTime = newTime;
    setCurrentTime(newTime);
    logTrace('VideoPlayer', 'video_skip_forward_' + segmentTitle, segmentTitle);
  }, [currentTime, duration, startTimeSeconds, endTimeSeconds, segmentTitle]);

  // Handle skip backward (10 seconds)
  const handleSkipBackward = useCallback((): void => {
    const video = videoRef.current;
    if (!video) return;

    const newTime = Math.max(currentTime - 10, startTimeSeconds);
    video.currentTime = newTime;
    setCurrentTime(newTime);
    logTrace('VideoPlayer', 'video_skip_backward_' + segmentTitle, segmentTitle);
  }, [currentTime, startTimeSeconds, segmentTitle]);

  // Handle seek via progress bar click
  const handleSeekClick = useCallback((event: React.MouseEvent<HTMLDivElement>): void => {
    const video = videoRef.current;
    if (!progressBarRef.current || !video) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = (percentage * segmentDuration) + startTimeSeconds;

    video.currentTime = newTime;
    setCurrentTime(newTime);
    logTrace('VideoPlayer', 'video_seek_' + segmentTitle, segmentTitle);
  }, [segmentDuration, startTimeSeconds, segmentTitle]);

  // Handle drag start on seek handle
  const handleDragStart = useCallback((event: React.MouseEvent | React.TouchEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  // Handle drag move
  const handleDragMove = useCallback((event: MouseEvent | TouchEvent): void => {
    const video = videoRef.current;
    if (!isDragging || !progressBarRef.current || !video) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    let clientX: number;

    if ('touches' in event) {
      clientX = event.touches[0].clientX;
    } else {
      clientX = event.clientX;
    }

    const offsetX = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = offsetX / rect.width;
    const newTime = (percentage * segmentDuration) + startTimeSeconds;

    setCurrentTime(newTime);
    video.currentTime = newTime;
  }, [isDragging, segmentDuration, startTimeSeconds]);

  // Handle drag end
  const handleDragEnd = useCallback((): void => {
    if (isDragging) {
      setIsDragging(false);
      logTrace('VideoPlayer', 'video_seek_' + segmentTitle, segmentTitle);
    }
  }, [isDragging, segmentTitle]);

  // Handle fullscreen toggle
  const handleFullscreenToggle = useCallback((): void => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
        logTrace('VideoPlayer', 'video_fullscreen_enter_' + segmentTitle, segmentTitle);
      }).catch((err) => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
        logTrace('VideoPlayer', 'video_fullscreen_exit_' + segmentTitle, segmentTitle);
      }).catch((err) => {
        console.error('Error attempting to exit fullscreen:', err);
      });
    }
  }, [segmentTitle]);

  // Handle bookmark
  const handleBookmark = useCallback((): void => {
    if (onBookmark) {
      onBookmark(currentTime);
      logTrace('VideoPlayer', 'video_bookmark_' + segmentTitle, segmentTitle);
    }
  }, [currentTime, onBookmark, segmentTitle]);

  // Handle close
  const handleClose = useCallback((): void => {
    if (onClose) {
      onClose();
      logTrace('VideoPlayer', 'video_close_' + segmentTitle, segmentTitle);
    }
  }, [onClose, segmentTitle]);

  // Show controls temporarily
  const showControlsTemporarily = useCallback((): void => {
    setShowControls(true);

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying && !isDragging) {
        setShowControls(false);
      }
    }, 3000);
  }, [isPlaying, isDragging]);

  // Handle container mouse move/touch
  const handleContainerInteraction = useCallback((): void => {
    showControlsTemporarily();
  }, [showControlsTemporarily]);

  // Video event handler: onLoadedMetadata - called when video metadata is loaded
  const handleLoadedMetadata = useCallback((e: SyntheticEvent<HTMLVideoElement>): void => {
    const video = e.currentTarget;
    setDuration(video.duration);
    const calculatedDuration = (endTimeSeconds && endTimeSeconds > startTimeSeconds)
      ? Math.min(endTimeSeconds, video.duration) - startTimeSeconds
      : video.duration - startTimeSeconds;
    setSegmentDuration(calculatedDuration);
    setIsReady(true);
    setIsBuffering(false);
    // Seek to start position
    if (startTimeSeconds > 0) {
      video.currentTime = startTimeSeconds;
    }
    logTrace('VideoPlayer', 'proprio_video_load_' + segmentTitle, segmentTitle);
  }, [startTimeSeconds, endTimeSeconds, segmentTitle]);

  // Video event handler: onTimeUpdate - called during playback
  const handleTimeUpdate = useCallback((e: SyntheticEvent<HTMLVideoElement>): void => {
    if (!isDragging) {
      const video = e.currentTarget;
      const effectiveEndTime = endTimeSeconds > startTimeSeconds ? endTimeSeconds : duration;
      // Stop at end time if specified
      if (video.currentTime >= effectiveEndTime && effectiveEndTime > 0) {
        video.pause();
        video.currentTime = effectiveEndTime;
        setIsPlaying(false);
        setShowControls(true);
      }
      setCurrentTime(video.currentTime);
    }
  }, [isDragging, endTimeSeconds, startTimeSeconds, duration]);

  // Video event handler: onWaiting - called when video starts buffering
  const handleWaiting = useCallback((): void => {
    setIsBuffering(true);
  }, []);

  // Video event handler: onCanPlay - called when video can play
  const handleCanPlay = useCallback((): void => {
    setIsBuffering(false);
  }, []);

  // Video event handler: onPlay - called when video starts playing
  const handleVideoPlay = useCallback((): void => {
    setIsPlaying(true);
  }, []);

  // Video event handler: onPause - called when video is paused
  const handleVideoPause = useCallback((): void => {
    setIsPlaying(false);
  }, []);

  // Video event handler: onEnded - called when video ends
  const handleVideoEnded = useCallback((): void => {
    setIsPlaying(false);
    setShowControls(true);
  }, []);

  // Video event handler: onError - called on playback error
  const handleVideoError = useCallback((event: SyntheticEvent<HTMLVideoElement>): void => {
    setIsBuffering(false);

    const mediaError = event.currentTarget.error;
    const errorCode = mediaError?.code;
    const errorMessage = mediaError?.message || 'Unknown video error';

    const errorCodeDescriptions: Record<number, string> = {
      1: 'MEDIA_ERR_ABORTED',
      2: 'MEDIA_ERR_NETWORK',
      3: 'MEDIA_ERR_DECODE',
      4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
    };

    const errorDescription = errorCode
      ? errorCodeDescriptions[errorCode] || `Unknown error code: ${errorCode}`
      : 'No error code available';

    // Log to CloudWatch RUM
    logError('VideoPlayer.loadError', new Error(`Video load failed: ${errorDescription}`));

    // Sanitize URL to remove query parameters and fragments (may contain sensitive tokens)
    const sanitizedUrl = videoUrl.split('?')[0].split('#')[0];

    // Log detailed event with context
    logEvent({
      context: 'video_load_error',
      caseId: caseId || 'unknown',
      videoUrl: sanitizedUrl,
      errorCode: errorCode,
      errorMessage: errorMessage,
      errorDescription: errorDescription,
    });

    setError('load_error');
  }, [caseId, videoUrl]);

  // Setup drag event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchmove', handleDragMove);
      document.addEventListener('touchend', handleDragEnd);
    }

    return (): void => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleDragMove);
      document.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = (): void => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return (): void => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return (): void => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  // Sync fullscreen mode prop
  useEffect(() => {
    setIsFullscreen(isFullscreenMode);
  }, [isFullscreenMode]);

  // Calculate progress percentage
  const progressPercentage = segmentDuration > 0 ? (getNormalizedTime() / segmentDuration) * 100 : 0;

  // Error state
  if (error === 'load_error') {
    return <VideoLoadError />;
  }

  return (
    <div
      ref={containerRef}
      className={`${videoStyles['player-container']} ${isFullscreen ? videoStyles['fullscreen'] : videoStyles['embedded']}`}
      onMouseMove={handleContainerInteraction}
      onMouseEnter={handleContainerInteraction}
      onTouchStart={handleContainerInteraction}
      onClick={handleContainerInteraction}
    >
      {/* Background overlay for fullscreen */}
      {isFullscreen && <div className={videoStyles['background-overlay']} />}

      {/* Video player */}
      <div className={videoStyles['video-wrapper']}>
        <ReactPlayer
          ref={videoRef}
          src={videoUrl}
          playing={isPlaying}
          controls={false}
          width="100%"
          height="100%"
          playsInline
          autoPlay={autoPlay}
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onWaiting={handleWaiting}
          onCanPlay={handleCanPlay}
          onPlay={handleVideoPlay}
          onPause={handleVideoPause}
          onEnded={handleVideoEnded}
          onError={handleVideoError}
        />
      </div>

      {/* Controls overlay */}
      <div className={`${videoStyles['controls-overlay']} ${showControls ? videoStyles['visible'] : videoStyles['hidden']}`}>
        {/* Top controls - fullscreen toggle and close button */}
        <div className={videoStyles['top-controls']}>
          {/* Left side - Procedure type header (only in fullscreen) */}
          <div className={videoStyles['top-left']}>
            {isFullscreen && procedureType && (
              <div className={videoStyles['procedure-header']}>
                {procedureType}
              </div>
            )}
          </div>

          {/* Right side - Action buttons */}
          <div className={videoStyles['top-right']}>
            {/* Bookmark button (for future use) */}
            {onBookmark && (
              <button
                className={videoStyles['control-button']}
                onClick={handleBookmark}
                aria-label="Bookmark this moment"
              >
                <Bookmark size={20} strokeWidth={1.5} />
              </button>
            )}

            {/* Fullscreen toggle button */}
            <button
              className={videoStyles['control-button']}
              onClick={handleFullscreenToggle}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? (
                <Minimize size={20} strokeWidth={2} />
              ) : (
                <Maximize size={20} strokeWidth={2} />
              )}
            </button>

            {/* Close button - only in fullscreen */}
            {isFullscreen && onClose && (
              <button
                className={videoStyles['close-button']}
                onClick={handleClose}
                aria-label="Close video player"
              >
                <X size={20} strokeWidth={2} />
              </button>
            )}
          </div>
        </div>

        {/* Center controls */}
        <div className={videoStyles['center-controls']}>
          <button
            className={videoStyles['skip-button']}
            onClick={handleSkipBackward}
            aria-label="Skip backward 10 seconds"
            disabled={!isReady}
          >
            {/* Custom skip previous icon |◀ */}
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 6V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M18 6L10 12L18 18V6Z" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            className={videoStyles['play-pause-button']}
            onClick={handlePlayPause}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            disabled={!isReady}
          >
            {isBuffering ? (
              <div className={videoStyles['spinner']} />
            ) : isPlaying ? (
              <Pause size={48} strokeWidth={2} />
            ) : (
              <Play size={48} strokeWidth={2} fill="white" />
            )}
          </button>
          <button
            className={videoStyles['skip-button']}
            onClick={handleSkipForward}
            aria-label="Skip forward 10 seconds"
            disabled={!isReady}
          >
            {/* Custom skip next icon ▶| */}
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M6 6L14 12L6 18V6Z" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Bottom controls */}
        <div className={videoStyles['bottom-controls']}>
          {/* Info bar with time and segment details as separate bars */}
          <div className={videoStyles['info-bar']}>
            <div className={videoStyles['playtime-bar']}>
              <span className={videoStyles['current-time']}>{formatTime(getNormalizedTime())}</span>
              <span className={videoStyles['time-separator']}>/</span>
              <span className={videoStyles['remaining-time']}>{getRemainingTime()}</span>
            </div>
            {segmentTitle && (
              <div className={videoStyles['segment-bar']}>
                {segmentTitle}
              </div>
            )}
          </div>

          {/* Progress bar / Timeline */}
          <div
            ref={progressBarRef}
            className={videoStyles['progress-bar']}
            onClick={handleSeekClick}
          >
            <div className={videoStyles['progress-track']}>
              <div
                className={videoStyles['progress-fill']}
                style={{ width: `${progressPercentage}%` }}
              />
              <div
                className={`${videoStyles['seek-handle']} ${isDragging ? videoStyles['dragging'] : ''}`}
                style={{ left: `${progressPercentage}%` }}
                onMouseDown={handleDragStart}
                onTouchStart={handleDragStart}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
